/**
 * CreateOrderDialog - Create a new manual draft order.
 *
 * Handles customer search, PO number, and ship date entry.
 * After creation, the parent opens the existing OrderDetailModal
 * for line-item editing and special pricing — no duplicate code path.
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type {
  DuplicatePoOrderSummary,
  PortalQuoteSummary,
  WarehouseOption,
} from '../../services/portalOrders';
import type { PortalManualOrderRequest } from '../../types/orders';
import ShipToSearch from './ShipToSearch';
import type { ShipToResult } from './ShipToSearch';
import NewSageShipToForm from './NewSageShipToForm';
import { portalService } from '../../services/portal';
import AddCustomerModal from './AddCustomerModal';
import SearchableDropdown from './SearchableDropdown';
import DuplicatePoWarning from './DuplicatePoWarning';
import { useClickOutside } from '../../hooks/useClickOutside';
import { buildWarehouseDropdownItems } from '../../data/warehouseDropdownItems';
import { shipDateBounds, todayIsoDate } from '../../utils/shipDateBounds';
import styles from './CreateOrderDialog.module.css';

// Row shape of GET /portal/customers/search (PortalCustomerSearchResult in
// app/routes/portal.py). `id` is the Customer PK and is the typeahead row key,
// so a fixture that omits it renders rows with key=undefined and React logs
// the "unique key prop" warning — type test fixtures with this to stay in sync.
export interface CustomerSearchResult {
  id: string;
  sage_customer_no: string;
  customer_name: string | null;
  city: string | null;
  state: string | null;
  salesperson_no: string | null;
  salesperson_name: string | null;
}

/**
 * Pre-selection for the "Create Order from Quote" entry point (customer 360
 * Quotes tab, kanban 19046830): the dialog opens with the customer already
 * picked and, when given, the quote already linked so its lines and prices
 * are copied into the draft on create.
 */
export interface CreateOrderPrefill {
  customer: CustomerSearchResult;
  quote?: PortalQuoteSummary | null;
}

interface CreateOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated: (orderId: string) => void;
  /** Open with a customer (and optionally a quote) pre-selected. */
  prefill?: CreateOrderPrefill | null;
}

export const CreateOrderDialog: React.FC<CreateOrderDialogProps> = ({
  isOpen,
  onClose,
  onOrderCreated,
  prefill = null,
}) => {
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerSearchResult[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [poNumber, setPoNumber] = useState('');
  const [requestedShipDate, setRequestedShipDate] = useState('');
  const [shipDateConfirmed, setShipDateConfirmed] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  // "No customers found" rescue: create the customer (Sage or local
  // placeholder) without leaving the order flow.
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [isJobsite, setIsJobsite] = useState(false);
  const [isBackorder, setIsBackorder] = useState(false);
  const [udfBl, setUdfBl] = useState('ppd.');
  const [chargeFreight, setChargeFreight] = useState(true);
  const [warehouseCode, setWarehouseCode] = useState('000');
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [selectedShipTo, setSelectedShipTo] = useState<ShipToResult | null>(null);
  const [showShipToSearch, setShowShipToSearch] = useState(false);
  // Inline "create a new Sage ship-to" (customer-scoped: the order doesn't
  // exist yet). Only offered when Edit-in-Sage writes are enabled.
  const [showNewShipTo, setShowNewShipTo] = useState(false);
  const [sageEnabled, setSageEnabled] = useState(false);
  const [customerQuotes, setCustomerQuotes] = useState<PortalQuoteSummary[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<PortalQuoteSummary | null>(
    null,
  );
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [duplicatePoOrders, setDuplicatePoOrders] = useState<DuplicatePoOrderSummary[]>([]);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duplicateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Read through a ref so the open-time reset below only re-runs on open,
  // never when the parent re-renders with an equal prefill mid-edit.
  const prefillRef = useRef<CreateOrderPrefill | null>(prefill);
  prefillRef.current = prefill;

  const warehouseItems = useMemo(
    () => buildWarehouseDropdownItems(warehouseOptions),
    [warehouseOptions],
  );

  // The picker's options: the customer's recent quotes, plus the selected
  // quote when it is not among them (a pre-selected quote older than the
  // list window must still render as the chosen option).
  const quoteOptions = useMemo<PortalQuoteSummary[]>(() => {
    if (selectedQuote && !customerQuotes.some((q) => q.id === selectedQuote.id)) {
      return [selectedQuote, ...customerQuotes];
    }
    return customerQuotes;
  }, [customerQuotes, selectedQuote]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCustomerQuery('');
      setCustomerResults([]);
      setSelectedCustomer(null);
      setPoNumber('');
      setRequestedShipDate('');
      setShipDateConfirmed(false);
      setIsJobsite(false);
      setIsBackorder(false);
      setWarehouseCode('000');
      setSelectedShipTo(null);
      setShowShipToSearch(false);
      setShowNewShipTo(false);
      setCustomerQuotes([]);
      setSelectedQuote(null);
      setDuplicatePoOrders([]);
      setError(null);
      setShowResults(false);
      const initial = prefillRef.current;
      if (initial) {
        // Entry point from a quote: the customer is fixed and the quote is
        // already linked; the customer's quote list still loads so the
        // picker can be changed or cleared.
        setSelectedCustomer(initial.customer);
        setCustomerQuery(
          initial.customer.customer_name || initial.customer.sage_customer_no,
        );
        setSelectedQuote(initial.quote ?? null);
      } else {
        setTimeout(() => inputRef.current?.focus(), 100);
      }

      // Fetch warehouse options
      portalOrderService.getWarehouses()
        .then(setWarehouseOptions)
        .catch(() => setWarehouseOptions([]));

      // Edit-in-Sage gate: the inline new-ship-to create is only offered
      // when Sage writes are enabled in this environment.
      portalService.getPortalConfig()
        .then((cfg) => setSageEnabled(Boolean(cfg.sage_edit_enabled)))
        .catch(() => setSageEnabled(false));
    }
  }, [isOpen]);

  // ── Customer search ──
  const handleCustomerSearch = useCallback((query: string) => {
    setCustomerQuery(query);
    setSelectedCustomer(null);
    setSelectedShipTo(null);
    setError(null);

    if (searchTimeout.current !== null) clearTimeout(searchTimeout.current);

    if (query.length < 2) {
      setCustomerResults([]);
      setShowResults(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { default: api } = await import('../../services/api');
        const response = await api.get<CustomerSearchResult[]>('/portal/customers/search', {
          params: { q: query, limit: 10 },
        });
        setCustomerResults(response.data);
        setShowResults(true);
      } catch {
        setCustomerResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  const handleSelectCustomer = useCallback((customer: CustomerSearchResult) => {
    setSelectedCustomer(customer);
    setCustomerQuery(customer.customer_name || customer.sage_customer_no);
    setShowResults(false);
    setCustomerResults([]);
    // Reset any prior quote selection when customer changes — quotes are
    // customer-scoped.
    setSelectedQuote(null);
  }, []);

  // Fetch available quotes for the selected customer.
  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerQuotes([]);
      return;
    }
    let cancelled = false;
    setIsLoadingQuotes(true);
    portalOrderService
      .listQuotesForCustomer(selectedCustomer.sage_customer_no)
      .then((quotes) => {
        if (!cancelled) setCustomerQuotes(quotes);
      })
      .catch(() => {
        if (!cancelled) setCustomerQuotes([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingQuotes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCustomer]);

  // ── Duplicate-PO check (debounced) ──
  // Runs whenever the (customer, PO) pair both have values. Non-blocking:
  // we surface a warning banner but the user can still create the order.
  useEffect(() => {
    if (duplicateTimeout.current !== null) {
      clearTimeout(duplicateTimeout.current);
    }
    const customerNo = selectedCustomer?.sage_customer_no;
    const trimmedPo = poNumber.trim();
    if (!customerNo || !trimmedPo) {
      setDuplicatePoOrders([]);
      return;
    }
    duplicateTimeout.current = setTimeout(async () => {
      try {
        const res = await portalOrderService.checkDuplicatePoOrders(
          customerNo,
          trimmedPo,
        );
        setDuplicatePoOrders(res.duplicates);
      } catch {
        setDuplicatePoOrders([]);
      }
    }, 350);
    return () => {
      if (duplicateTimeout.current !== null) clearTimeout(duplicateTimeout.current);
    };
  }, [selectedCustomer, poNumber]);

  const handleViewExistingOrder = useCallback(
    (orderId: string) => {
      // Reuse the same handoff path as a freshly-created order: parent
      // closes the dialog and opens OrderDetailModal for editing.
      onOrderCreated(orderId);
      onClose();
    },
    [onOrderCreated, onClose],
  );

  // ── Create order ──
  const handleCreate = useCallback(async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const data: PortalManualOrderRequest = {
        customer_no: selectedCustomer.sage_customer_no,
        customer_name: selectedCustomer.customer_name || selectedCustomer.sage_customer_no,
        salesperson_no: selectedCustomer.salesperson_no || undefined,
        salesperson_name: selectedCustomer.salesperson_name || undefined,
        po_number: poNumber || undefined,
        requested_ship_date: requestedShipDate || undefined,
        requested_ship_date_confirmed: requestedShipDate ? shipDateConfirmed : undefined,
        udf_jobsite: isJobsite ? 'Y' : 'N',
        order_type: isBackorder ? 'B' : 'S',
        udf_bl: udfBl,
        charge_freight: chargeFreight,
        warehouse_code: warehouseCode || '000',
        ship_to_address: selectedShipTo ? {
          ship_to_code: selectedShipTo.ship_to_code,
          ship_to_name: selectedShipTo.ship_to_name,
          address1: selectedShipTo.address1,
          address2: selectedShipTo.address2 ?? null,
          address3: selectedShipTo.address3 ?? null,
          city: selectedShipTo.city,
          state: selectedShipTo.state,
          zip_code: selectedShipTo.zip_code,
          warehouse_code: selectedShipTo.warehouse_code ?? null,
        } : undefined,
        sales_quote_id: selectedQuote ? selectedQuote.id : undefined,
        udf_quote: selectedQuote ? String(selectedQuote.source_id) : undefined,
      };

      const result = await portalOrderService.createManualOrder(data);
      const orderId = result.order.id;

      // Close dialog and let the parent open OrderDetailModal for line-item editing
      onOrderCreated(orderId);
      onClose();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to create order'
        : 'Failed to create order';
      setError(message);
    } finally {
      setIsCreating(false);
    }
  }, [selectedCustomer, selectedShipTo, selectedQuote, poNumber, requestedShipDate, shipDateConfirmed, isJobsite, isBackorder, udfBl, chargeFreight, warehouseCode, onOrderCreated, onClose]);

  // ── Keyboard ──
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showResults) {
        setShowResults(false);
      } else {
        onClose();
      }
    }
  }, [showResults, onClose]);

  const closeResults = useCallback(() => setShowResults(false), []);
  useClickOutside(resultsRef, closeResults);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onKeyDown={handleKeyDown}>
      <div className={styles.dialog} data-section="new_order_form">
        <div className={styles.header}>
          <h2>
            {prefill?.quote
              ? `Create Order from Quote #${prefill.quote.source_id}`
              : 'Create New Order'}
          </h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close" type="button" data-action="new_order_close" data-outcome="close">
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {/* Customer Search */}
          <div className={styles.field} data-section="new_order_customer">
            <label className={styles.label}>
              Customer <span className={styles.required}>*</span>
            </label>
            <div className={styles.searchContainer} ref={resultsRef}>
              <input
                ref={inputRef}
                type="text"
                className={styles.input}
                placeholder="Search by customer name or number..."
                value={customerQuery}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                disabled={isCreating}
              />
              {isSearching && <span className={styles.spinner} />}
              {showResults && customerResults.length > 0 && (
                <div className={styles.results}>
                  {customerResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={styles.resultItem}
                      data-action="customer_pick"
                      data-target-customer-no={c.sage_customer_no}
                      onClick={() => handleSelectCustomer(c)}
                    >
                      <span className={styles.customerName}>
                        {c.customer_name || 'Unknown'}
                      </span>
                      <span className={styles.customerMeta}>
                        {c.sage_customer_no}
                        {c.city && c.state ? ` · ${c.city}, ${c.state}` : ''}
                        {c.salesperson_name ? ` · ${c.salesperson_name}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {showResults && customerResults.length === 0 && !isSearching && customerQuery.length >= 2 && (
                <div className={styles.results}>
                  <div className={styles.noResults}>No customers found</div>
                  {/* Dead-end rescue: create the customer right here (Sage or
                      local placeholder) instead of abandoning the order. */}
                  <button
                    type="button"
                    className={styles.resultItem}
                    data-testid="createorder-add-customer"
                    data-action="customer_add_new_open"
                    onClick={() => setShowAddCustomer(true)}
                    style={{ color: '#2563eb', fontWeight: 600 }}
                  >
                    + Add new customer &ldquo;{customerQuery}&rdquo;
                  </button>
                </div>
              )}
            </div>
            {selectedCustomer && (
              <div className={styles.selectedBadge}>
                <span className={styles.badgeIcon}>&#10003;</span>
                {selectedCustomer.customer_name} ({selectedCustomer.sage_customer_no})
                {selectedCustomer.salesperson_name && (
                  <span className={styles.badgeSalesperson}>
                    &nbsp;&middot; Rep: {selectedCustomer.salesperson_name}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Ship-To Address */}
          <div className={styles.field} data-section="new_order_ship_to">
            <label className={styles.label}>Ship-To Address</label>
            {!selectedCustomer ? (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '8px 0' }}>
                Select a customer first
              </div>
            ) : selectedShipTo && !showShipToSearch ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, color: '#166534' }}>
                <span>&#10003;</span>
                <span>
                  {selectedShipTo.ship_to_name || selectedShipTo.ship_to_code}
                  <span style={{ color: '#4b5563', marginLeft: 4 }}>
                    &mdash; {selectedShipTo.address1}
                    {selectedShipTo.address2 && `, ${selectedShipTo.address2}`}
                    {selectedShipTo.address3 && `, ${selectedShipTo.address3}`}
                    {selectedShipTo.city && `, ${selectedShipTo.city}`}
                    {selectedShipTo.state && `, ${selectedShipTo.state}`}
                    {selectedShipTo.zip_code && ` ${selectedShipTo.zip_code}`}
                  </span>
                </span>
                {!isCreating && (
                  <button
                    type="button"
                    style={{ marginLeft: 'auto', border: 'none', background: 'none', color: 'var(--primary, #0046AD)', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '2px 6px', borderRadius: 4 }}
                    onClick={() => setShowShipToSearch(true)}
                  >
                    Change
                  </button>
                )}
              </div>
            ) : showNewShipTo ? (
              <NewSageShipToForm
                customerNo={selectedCustomer.sage_customer_no}
                onCreated={(addr) => {
                  setSelectedShipTo({
                    ship_to_code: addr.ship_to_code,
                    ship_to_name: addr.ship_to_name,
                    address1: addr.address1,
                    address2: addr.address2 || undefined,
                    address3: addr.address3 || undefined,
                    city: addr.city,
                    state: addr.state,
                    zip_code: addr.zip_code,
                  });
                  setShowNewShipTo(false);
                  setShowShipToSearch(false);
                }}
                onCancel={() => setShowNewShipTo(false)}
              />
            ) : (
              <ShipToSearch
                customerNo={selectedCustomer.sage_customer_no}
                customerName={selectedCustomer.customer_name ?? undefined}
                onSelect={(addr) => {
                  setSelectedShipTo(addr);
                  setShowShipToSearch(false);
                }}
                onCancel={() => setShowShipToSearch(false)}
                // Rendered by ShipToSearch inside the dropdown footer while
                // open (the dropdown is open in every state, so a sibling
                // button below would be covered and unclickable — the
                // PR #1755 defect class).
                onCreateNew={sageEnabled ? () => setShowNewShipTo(true) : undefined}
              />
            )}
          </div>

          {/* Linked Sales Quote */}
          <div className={styles.field} data-section="new_order_quote">
            <label className={styles.label}>
              Linked Quote
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                (optional — pre-fills line items &amp; prices)
              </span>
            </label>
            {!selectedCustomer ? (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '8px 0' }}>
                Select a customer first
              </div>
            ) : isLoadingQuotes ? (
              <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 0' }}>
                Loading quotes…
              </div>
            ) : quoteOptions.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                No sales quotes found for this customer
              </div>
            ) : (
              <>
                <select
                  className={styles.input}
                  value={selectedQuote ? selectedQuote.id : ''}
                  onChange={(e) => {
                    const next = quoteOptions.find((q) => q.id === e.target.value) ?? null;
                    setSelectedQuote(next);
                  }}
                  disabled={isCreating}
                  data-testid="create-order-quote-picker"
                >
                  <option value="">— No linked quote —</option>
                  {quoteOptions.map((q) => (
                    <option key={q.id} value={q.id}>
                      #{q.source_id}
                      {q.project_name ? ` · ${q.project_name}` : ''}
                      {' · '}
                      {q.product_count} item{q.product_count === 1 ? '' : 's'}
                      {q.estimated_total !== null
                        ? ` · $${q.estimated_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : ''}
                      {' · '}
                      {q.created_date.slice(0, 10)}
                    </option>
                  ))}
                </select>
                {selectedQuote && (
                  <div
                    style={{
                      marginTop: 6,
                      padding: '6px 10px',
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#1e3a8a',
                    }}
                  >
                    <strong>UDF_QUOTE &rarr; Sage:</strong>{' '}
                    <code style={{ background: '#dbeafe', padding: '1px 4px', borderRadius: 3 }}>
                      {selectedQuote.source_id}
                    </code>
                    <br />
                    Line items &amp; prices from this quote will be copied into the draft order.
                  </div>
                )}
              </>
            )}
          </div>

          {/* PO Number */}
          <div className={styles.field} data-section="new_order_po_number">
            <label className={styles.label}>PO Number</label>
            <input
              type="text"
              className={styles.input}
              placeholder="Customer PO reference (optional)"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              disabled={isCreating}
            />
            {duplicatePoOrders.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <DuplicatePoWarning
                  duplicates={duplicatePoOrders}
                  onViewOrder={handleViewExistingOrder}
                />
              </div>
            )}
          </div>

          {/* Requested Ship Date */}
          <div className={styles.field} data-section="new_order_ship_date">
            <label className={styles.label}>Requested Ship Date</label>
            {!requestedShipDate ? (
              <button
                type="button"
                onClick={() => {
                  setRequestedShipDate(todayIsoDate());
                }}
                disabled={isCreating}
                style={{
                  background: 'none',
                  border: '1px dashed #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                + Add ship date (only if customer requested)
              </button>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="date"
                    className={styles.input}
                    style={{ flex: 1 }}
                    min={shipDateBounds().min}
                    max={shipDateBounds().max}
                    value={requestedShipDate}
                    onChange={(e) => {
                      setRequestedShipDate(e.target.value);
                      if (!e.target.value) setShipDateConfirmed(false);
                    }}
                    disabled={isCreating}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setRequestedShipDate('');
                      setShipDateConfirmed(false);
                    }}
                    disabled={isCreating}
                    style={{
                      background: 'none',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      fontSize: '13px',
                      color: '#6b7280',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="Clear ship date"
                  >
                    ✕ Clear
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '13px', color: shipDateConfirmed ? '#059669' : '#d97706' }}>
                  <input
                    type="checkbox"
                    checked={shipDateConfirmed}
                    onChange={(e) => setShipDateConfirmed(e.target.checked)}
                    disabled={isCreating}
                  />
                  I confirm the customer specifically requested this delivery date
                </label>
              </>
            )}
          </div>

          {/* Warehouse + Jobsite row */}
          <div className={styles.fieldRow} data-section="new_order_shipping_options">
            {/* Warehouse dropdown (searchable by code or description) */}
            <div className={styles.fieldHalf}>
              <label className={styles.label}>Warehouse</label>
              <SearchableDropdown
                value={warehouseCode}
                onChange={(next) => setWarehouseCode(next || '000')}
                items={warehouseItems}
                placeholder="Type code or name..."
                maxLength={3}
                disabled={isCreating}
                inputClassName={styles.input}
                testId="create-order-warehouse"
                highlightedLabel="Active"
                otherLabel="Other"
                record={{ action: 'create_order_warehouse_pick', valueKey: 'warehouse_code' }}
              />
            </div>

            {/* Jobsite checkbox */}
            <div className={styles.fieldHalf}>
              <label className={styles.label}>Jobsite</label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isJobsite}
                  onChange={(e) => setIsJobsite(e.target.checked)}
                  disabled={isCreating}
                  className={styles.checkbox}
                />
                Jobsite delivery
              </label>
            </div>
          </div>

          <div className={styles.fieldRow}>
            {/* Bill of Lading */}
            <div className={styles.fieldHalf}>
              <label className={styles.label}>Bill of Lading</label>
              <select
                value={udfBl}
                onChange={(e) => setUdfBl(e.target.value)}
                disabled={isCreating}
                className={styles.input}
              >
                <option value="ppd.">Prepaid</option>
                <option value="col.">Collect</option>
                <option value="3rd party">3rd Party</option>
              </select>
            </div>

            {/* Backorder checkbox */}
            <div className={styles.fieldHalf}>
              <label className={styles.label}>Backorder</label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isBackorder}
                  onChange={(e) => setIsBackorder(e.target.checked)}
                  disabled={isCreating}
                  className={styles.checkbox}
                  data-testid="create-order-backorder"
                />
                This is a backorder
              </label>
            </div>
          </div>

          <div className={styles.fieldRow}>
            {/* Charge freight at invoicing */}
            <div className={styles.fieldHalf}>
              <label className={styles.label}>Freight Billing</label>
              <label className={styles.checkboxLabel} title="Uncheck to waive freight: the order will carry a DO NOT BILL FREIGHT note to the invoicing desk. (Collect / 3rd-party shipments never bill freight regardless.)">
                <input
                  type="checkbox"
                  checked={chargeFreight}
                  onChange={(e) => setChargeFreight(e.target.checked)}
                  disabled={isCreating}
                  className={styles.checkbox}
                  data-testid="create-order-charge-freight"
                />
                Charge freight when invoiced
              </label>
            </div>
          </div>

          {error && (
            <div className={styles.error}>{error}</div>
          )}
        </div>

        <div className={styles.footer} data-section="new_order_footer">
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={isCreating}
            type="button"
            data-action="new_order_cancel"
            data-outcome="close"
          >
            Cancel
          </button>
          <button
            className={styles.createBtn}
            onClick={handleCreate}
            disabled={!selectedCustomer || isCreating}
            type="button"
            data-action="new_order_submit"
            data-target-customer-no={selectedCustomer?.sage_customer_no ?? undefined}
            data-outcome="request"
          >
            {isCreating ? 'Creating...' : 'Create Draft Order'}
          </button>
        </div>
      </div>

      {showAddCustomer && (
        <AddCustomerModal
          initialName={customerQuery}
          onCreated={(customerNo) => {
            setShowAddCustomer(false);
            // Re-search by the new account number — it appears once Sage
            // confirms (~2 min); the create modal already said so.
            handleCustomerSearch(customerNo);
          }}
          onCancel={() => setShowAddCustomer(false)}
        />
      )}
    </div>
  );
};

export default CreateOrderDialog;
